<template>
    <AdminLayout>
        <template #header>
            <Header :menu="$page.props.main_menu" v-if="$page.props.auth.user" />
        </template>
        <template #default>
            <div class="mt-10 space-y-6">
                <!-- Header -->
                <div class="flex items-center justify-between">
                    <h1 class="text-2xl font-bold text-gray-900">Pengumuman</h1>
                    <Link :href="route('pengumuman.create')"
                        class="inline-flex items-center px-4 py-2 text-xs font-semibold tracking-widest text-white uppercase transition duration-150 ease-in-out bg-yellow-600 border border-transparent rounded-md hover:bg-yellow-700">
                    <i class="mr-2 fas fa-plus"></i>
                    Tambah Pengumuman
                    </Link>
                </div>

                <!-- Filter Section -->
                <div class="p-6 bg-white rounded-lg shadow-lg">

                    <form @submit.prevent="applyFilters" class="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <div>
                            <label for="filter_judul" class="block mb-1 text-sm font-medium text-gray-700">
                                Judul Pengumuman
                            </label>
                            <input type="text" id="filter_judul" v-model="filters.judul" placeholder="Cari judul..."
                                class="block w-full border-gray-300 rounded-md shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm">
                        </div>

                        <div>
                            <label for="filter_prioritas" class="block mb-1 text-sm font-medium text-gray-700">
                                Prioritas
                            </label>
                            <select id="filter_prioritas" v-model="filters.prioritas"
                                class="block w-full border-gray-300 rounded-md shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm">
                                <option value="">Semua Prioritas</option>
                                <option value="rendah">Rendah</option>
                                <option value="sedang">Sedang</option>
                                <option value="tinggi">Tinggi</option>
                                <option value="urgent">Urgent</option>
                            </select>
                        </div>

                        <div>
                            <label for="filter_status" class="block mb-1 text-sm font-medium text-gray-700">
                                Status
                            </label>
                            <select id="filter_status" v-model="filters.status"
                                class="block w-full border-gray-300 rounded-md shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm">
                                <option value="">Semua Status</option>
                                <option value="draft">Draft</option>
                                <option value="aktif">Aktif</option>
                                <option value="nonaktif">Non Aktif</option>
                            </select>
                        </div>

                        <div class="flex items-end space-x-2">
                            <button type="submit"
                                class="inline-flex items-center justify-center flex-1 px-4 py-2 text-xs font-semibold tracking-widest text-white uppercase transition duration-150 ease-in-out bg-gray-600 border border-transparent rounded-md hover:bg-gray-700">
                                <i class="mr-2 fas fa-search"></i>
                                Filter
                            </button>
                            <button type="button" @click="resetFilters"
                                class="inline-flex items-center justify-center px-4 py-2 text-xs font-semibold tracking-widest text-gray-700 uppercase transition duration-150 ease-in-out bg-gray-300 border border-transparent rounded-md hover:bg-gray-400">
                                <i class="mr-2 fas fa-undo"></i>
                                Reset
                            </button>
                        </div>
                    </form>
                </div>



                <!-- Data Cards -->
                <div v-if="pengumuman.data.length === 0" class="py-12 text-center">
                    <i class="mb-4 text-5xl text-gray-400 fas fa-bullhorn"></i>
                    <h3 class="mb-2 text-lg font-medium text-gray-900">Tidak ada pengumuman</h3>
                    <p class="mb-6 text-gray-500">Belum ada pengumuman yang tersedia dengan filter yang dipilih.</p>
                    <Link :href="route('pengumuman.create')"
                        class="inline-flex items-center px-4 py-2 text-xs font-semibold tracking-widest text-white uppercase bg-yellow-600 border border-transparent rounded-md hover:bg-yellow-700">
                    <i class="mr-2 fas fa-plus"></i>
                    Tambah Pengumuman Pertama
                    </Link>
                </div>

                <div v-else class="grid grid-cols-1 gap-6 lg:grid-cols-2">
                    <div v-for="item in pengumuman.data" :key="item.id"
                        class="overflow-hidden bg-white border-l-4 rounded-lg shadow-lg"
                        :class="getPriorityBorderClass(item.prioritas)">
                        <div class="p-6">
                            <div class="flex items-start justify-between mb-4">
                                <div class="flex-1">
                                    <div class="flex items-center mb-2">
                                        <span :class="getPriorityBadgeClass(item.prioritas)"
                                            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mr-3">
                                            <i :class="getPriorityIcon(item.prioritas)" class="mr-1"></i>
                                            {{ getPriorityLabel(item.prioritas) }}
                                        </span>
                                        <span :class="getStatusBadgeClass(item.status)"
                                            class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium">
                                            {{ getStatusLabel(item.status) }}
                                        </span>
                                    </div>
                                    <h3 class="mb-2 text-lg font-medium text-gray-900">
                                        {{ item.judul }}
                                    </h3>
                                    <p class="mb-3 text-sm text-gray-600">
                                        {{ truncateText(item.konten, 150) }}
                                    </p>
                                    <div class="space-y-1 text-sm text-gray-500">
                                        <div class="flex items-center">
                                            <i class="w-4 mr-2 text-center fas fa-calendar"></i>
                                            Publikasi: {{ formatDate(item.tanggal_publikasi) }}
                                        </div>
                                        <div v-if="item.tanggal_berakhir" class="flex items-center">
                                            <i class="w-4 mr-2 text-center fas fa-calendar-times"></i>
                                            Berakhir: {{ formatDate(item.tanggal_berakhir) }}
                                        </div>
                                        <div class="flex items-center">
                                            <i class="w-4 mr-2 text-center fas fa-user"></i>
                                            {{ item.penerbit }}
                                        </div>
                                        <div v-if="item.file_lampiran" class="flex items-center">
                                            <i class="w-4 mr-2 text-center fas fa-paperclip"></i>
                                            Ada lampiran
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="flex items-center justify-between pt-4 border-t border-gray-200">
                                <div class="text-xs text-gray-500">
                                    Dibuat: {{ formatDateTime(item.created_at) }}
                                </div>
                                <div class="flex items-center space-x-2">
                                    <button @click="viewPengumuman(item)" class="text-gray-600 hover:text-gray-900"
                                        title="Lihat">
                                        <i class="fas fa-eye"></i>
                                    </button>
                                    <Link :href="route('pengumuman.edit', item.id)"
                                        class="text-blue-600 hover:text-blue-900" title="Edit">
                                    <i class="fas fa-edit"></i>
                                    </Link>
                                    <button @click="confirmDelete(item)" class="text-red-600 hover:text-red-900"
                                        title="Hapus">
                                        <i class="fas fa-trash"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Pagination -->
                <div v-if="pengumuman.data.length > 0" class="overflow-hidden bg-white rounded-lg shadow-lg">
                    <div class="px-4 py-3 border-t border-gray-200 sm:px-6">
                        <div class="flex items-center justify-between">
                            <div class="flex justify-between flex-1 sm:hidden">
                                <Link v-if="pengumuman.prev_page_url" :href="pengumuman.prev_page_url"
                                    class="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                                Previous
                                </Link>
                                <Link v-if="pengumuman.next_page_url" :href="pengumuman.next_page_url"
                                    class="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">
                                Next
                                </Link>
                            </div>
                            <div class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p class="text-sm text-gray-700">
                                        Menampilkan
                                        <span class="font-medium">{{ pengumuman.from || 0 }}</span>
                                        sampai
                                        <span class="font-medium">{{ pengumuman.to || 0 }}</span>
                                        dari
                                        <span class="font-medium">{{ pengumuman.total }}</span>
                                        hasil
                                    </p>
                                </div>
                                <div>
                                    <nav class="relative z-0 inline-flex -space-x-px rounded-md shadow-sm"
                                        aria-label="Pagination">
                                        <!-- Previous Page Link -->
                                        <Link v-if="pengumuman.prev_page_url" :href="pengumuman.prev_page_url"
                                            class="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-l-md hover:bg-gray-50">
                                        <span class="sr-only">Previous</span>
                                        <i class="fas fa-chevron-left"></i>
                                        </Link>

                                        <!-- Page Numbers -->
                                        <template v-for="link in pengumuman.links.slice(1, -1)" :key="link.label">
                                            <Link v-if="link.url" :href="link.url" :class="[
                                                link.active
                                                    ? 'z-10 bg-yellow-50 border-yellow-500 text-yellow-600'
                                                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                                                'relative inline-flex items-center px-4 py-2 border text-sm font-medium'
                                            ]">
                                            {{ link.label }}
                                            </Link>
                                            <span v-else
                                                class="relative inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300">
                                                {{ link.label }}
                                            </span>
                                        </template>

                                        <!-- Next Page Link -->
                                        <Link v-if="pengumuman.next_page_url" :href="pengumuman.next_page_url"
                                            class="relative inline-flex items-center px-2 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-r-md hover:bg-gray-50">
                                        <span class="sr-only">Next</span>
                                        <i class="fas fa-chevron-right"></i>
                                        </Link>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Delete Confirmation Modal -->
            <div v-if="showDeleteModal" class="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title"
                role="dialog" aria-modal="true">
                <div class="flex items-end justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                    <div class="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" @click="closeDeleteModal">
                    </div>

                    <span class="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

                    <div
                        class="relative inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                        <div class="px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
                            <div class="sm:flex sm:items-start">
                                <div
                                    class="flex items-center justify-center flex-shrink-0 w-12 h-12 mx-auto bg-red-100 rounded-full sm:mx-0 sm:h-10 sm:w-10">
                                    <i class="text-red-600 fas fa-exclamation-triangle"></i>
                                </div>
                                <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                                    <h3 class="text-lg font-medium leading-6 text-gray-900" id="modal-title">
                                        Konfirmasi Hapus
                                    </h3>
                                    <div class="mt-2">
                                        <p class="text-sm text-gray-500">
                                            Apakah Anda yakin ingin menghapus pengumuman
                                            <strong>{{ itemToDelete?.judul }}</strong>?
                                            Tindakan ini tidak dapat dibatalkan.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="px-4 py-3 bg-gray-50 sm:px-6 sm:flex sm:flex-row-reverse">
                            <button @click="deleteItem" type="button"
                                class="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-red-600 border border-transparent rounded-md shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm">
                                <i class="mr-2 fas fa-trash"></i>
                                Hapus
                            </button>
                            <button @click="closeDeleteModal" type="button"
                                class="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm">
                                Batal
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <template #footer></template>
    </AdminLayout>
</template>

<script setup>
import { ref, reactive } from 'vue'

import AdminLayout from '@/Layouts/Container.vue'
import Header from "@/Components/Header.vue";

const props = defineProps({
    pengumuman: Object,


})

const filters = reactive({
    judul: '',
    prioritas: '',
    status: ''
})

const showDeleteModal = ref(false)
const itemToDelete = ref(null)

const applyFilters = () => {
    router.get(route('pengumuman.index'), filters, {
        preserveState: true,
        preserveScroll: true
    })
}

const resetFilters = () => {
    Object.keys(filters).forEach(key => {
        filters[key] = ''
    })
    router.get(route('pengumuman.index'), {}, {
        preserveState: true,
        preserveScroll: true
    })
}

const viewPengumuman = (item) => {
    router.visit(route('pengumuman.show', item.id))
}

const confirmDelete = (item) => {
    itemToDelete.value = item
    showDeleteModal.value = true
}

const closeDeleteModal = () => {
    showDeleteModal.value = false
    itemToDelete.value = null
}

const deleteItem = () => {
    if (itemToDelete.value) {
        router.delete(route('pengumuman.destroy', itemToDelete.value.id), {
            onSuccess: () => {
                closeDeleteModal()
            }
        })
    }
}

const getPriorityBorderClass = (prioritas) => {
    const classes = {
        'urgent': 'border-red-500',
        'tinggi': 'border-orange-500',
        'sedang': 'border-yellow-500',
        'rendah': 'border-green-500'
    }
    return classes[prioritas] || 'border-gray-300'
}

const getPriorityBadgeClass = (prioritas) => {
    const classes = {
        'urgent': 'bg-red-100 text-red-800',
        'tinggi': 'bg-orange-100 text-orange-800',
        'sedang': 'bg-yellow-100 text-yellow-800',
        'rendah': 'bg-green-100 text-green-800'
    }
    return classes[prioritas] || 'bg-gray-100 text-gray-800'
}

const getPriorityIcon = (prioritas) => {
    const icons = {
        'urgent': 'fas fa-exclamation-triangle',
        'tinggi': 'fas fa-exclamation-circle',
        'sedang': 'fas fa-info-circle',
        'rendah': 'fas fa-check-circle'
    }
    return icons[prioritas] || 'fas fa-circle'
}

const getPriorityLabel = (prioritas) => {
    const labels = {
        'urgent': 'Urgent',
        'tinggi': 'Tinggi',
        'sedang': 'Sedang',
        'rendah': 'Rendah'
    }
    return labels[prioritas] || prioritas
}

const getStatusBadgeClass = (status) => {
    const classes = {
        'draft': 'bg-gray-100 text-gray-800',
        'aktif': 'bg-green-100 text-green-800',
        'nonaktif': 'bg-red-100 text-red-800'
    }
    return classes[status] || 'bg-gray-100 text-gray-800'
}

const getStatusLabel = (status) => {
    const labels = {
        'draft': 'Draft',
        'aktif': 'Aktif',
        'nonaktif': 'Non Aktif'
    }
    return labels[status] || status
}

const formatDate = (dateString) => {
    if (!dateString) return '-'
    const date = new Date(dateString)
    return date.toLocaleDateString('id-ID', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
    })
}

const formatDateTime = (dateTimeString) => {
    if (!dateTimeString) return '-'
    const date = new Date(dateTimeString)
    return date.toLocaleString('id-ID', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

const truncateText = (text, limit) => {
    if (!text) return ''
    return text.length > limit ? text.substring(0, limit) + '...' : text
}
</script>